// src/pages/Analytics.js
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiEye, FiTrendingUp, FiUsers, FiMousePointer, FiFolder, FiExternalLink } from 'react-icons/fi';
import { trackEvent } from '../utils/analytics';

export default function Analytics() {
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [stats, setStats] = useState({
    totalViews: 0,
    uniqueVisitors: 0,
    avgTimeOnSite: 0,
    bounceRate: 0,
    topPages: [],
    topProjects: [],
    trafficSources: []
  });

  // Simple password protection (in production, use proper authentication)
  const ANALYTICS_PASSWORD = 'munji2025'; // Change this!

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ANALYTICS_PASSWORD) {
      setAuthenticated(true);
      setError('');
      localStorage.setItem('analytics_auth', 'true');
      trackEvent('analytics_access', {
        event_category: 'Analytics',
        event_label: 'Dashboard Accessed'
      });
    } else {
      setError('Incorrect password');
    }
  };

  // Check if already authenticated
  useEffect(() => {
    const isAuth = localStorage.getItem('analytics_auth') === 'true';
    setAuthenticated(isAuth);
  }, []);

  // Fetch analytics data (mock data for now - replace with actual GA4 API)
  useEffect(() => {
    if (authenticated) {
      // Mock data - replace with actual Google Analytics API calls
      setStats({
        totalViews: 1247,
        uniqueVisitors: 856,
        avgTimeOnSite: 145, // seconds
        bounceRate: 32.5,
        topPages: [
          { path: '/', views: 445, title: 'Home' },
          { path: '/projects', views: 312, title: 'Projects' },
          { path: '/timeline', views: 198, title: 'Timeline' },
          { path: '/resume', views: 167, title: 'Resume' },
          { path: '/contact', views: 125, title: 'Contact' }
        ],
        topProjects: [
          { title: 'Potato Disease Detection', views: 89, clicks: 34 },
          { title: 'Portfolio Website', views: 76, clicks: 28 },
          { title: 'Smart Traffic Light Control', views: 65, clicks: 22 },
          { title: 'BeSafe Platform', views: 58, clicks: 19 },
          { title: 'NFC Student Food System', views: 54, clicks: 17 }
        ],
        trafficSources: [
          { source: 'Direct', visits: 445, percentage: 35.7 },
          { source: 'Google Search', visits: 324, percentage: 26.0 },
          { source: 'LinkedIn', visits: 267, percentage: 21.4 },
          { source: 'GitHub', visits: 134, percentage: 10.7 },
          { source: 'Other', visits: 77, percentage: 6.2 }
        ]
      });
    }
  }, [authenticated]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleLogout = () => {
    setAuthenticated(false);
    localStorage.removeItem('analytics_auth');
    setPassword('');
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-8 max-w-md w-full"
        >
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full mb-4">
              <FiTrendingUp className="text-3xl text-blue-600 dark:text-blue-400" />
            </div>
            <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-2">
              Analytics Dashboard
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Enter password to access analytics
            </p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                className="w-full px-4 py-3 border-2 border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 placeholder:text-gray-400 dark:placeholder:text-slate-400"
                required
              />
            </div>

            {error && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="p-3 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-lg text-sm text-center"
              >
                {error}
              </motion.div>
            )}

            <button
              type="submit"
              className="w-full px-4 py-3 bg-blue-600 dark:bg-blue-500 text-white rounded-lg font-semibold hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors"
            >
              Access Dashboard
            </button>
          </form>

          <p className="mt-6 text-xs text-center text-gray-500 dark:text-gray-400">
            🔒 Protected area - Authorized access only
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-900 dark:to-slate-800 py-12 px-4 transition-colors duration-300">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800 dark:text-gray-100 mb-2">
              📈 Analytics Dashboard
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Portfolio performance metrics and insights
            </p>
          </div>
          <button
            onClick={handleLogout}
            className="px-4 py-2 bg-red-600 dark:bg-red-500 text-white rounded-lg hover:bg-red-700 dark:hover:bg-red-600 transition-colors text-sm font-medium"
          >
            Logout
          </button>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <MetricCard
            icon={<FiEye />}
            title="Total Page Views"
            value={stats.totalViews.toLocaleString()}
            change="+12.5%"
            positive={true}
            color="blue"
          />
          <MetricCard
            icon={<FiUsers />}
            title="Unique Visitors"
            value={stats.uniqueVisitors.toLocaleString()}
            change="+8.3%"
            positive={true}
            color="green"
          />
          <MetricCard
            icon={<FiMousePointer />}
            title="Avg. Time on Site"
            value={formatTime(stats.avgTimeOnSite)}
            change="+15.2%"
            positive={true}
            color="purple"
          />
          <MetricCard
            icon={<FiTrendingUp />}
            title="Bounce Rate"
            value={`${stats.bounceRate}%`}
            change="-5.1%"
            positive={true}
            color="orange"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Top Pages */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg p-6 transition-colors duration-300">
            <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-6 flex items-center gap-2">
              <FiFolder className="text-blue-600 dark:text-blue-400" />
              Top Pages
            </h2>
            <div className="space-y-4">
              {stats.topPages.map((page, index) => (
                <div key={page.path} className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1">
                    <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-sm font-bold text-blue-600 dark:text-blue-400">
                      {index + 1}
                    </div>
                    <div>
                      <div className="font-semibold text-gray-800 dark:text-gray-100">{page.title}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{page.path}</div>
                    </div>
                  </div>
                  <div className="text-lg font-bold text-gray-800 dark:text-gray-100">
                    {page.views.toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Top Projects */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg p-6 transition-colors duration-300">
            <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-6 flex items-center gap-2">
              <FiExternalLink className="text-purple-600 dark:text-purple-400" />
              Top Projects
            </h2>
            <div className="space-y-4">
              {stats.topProjects.map((project, index) => (
                <div key={project.title} className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1">
                    <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-sm font-bold text-purple-600 dark:text-purple-400">
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold text-gray-800 dark:text-gray-100 text-sm">{project.title}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {project.clicks} clicks
                      </div>
                    </div>
                  </div>
                  <div className="text-sm font-bold text-gray-800 dark:text-gray-100">
                    {project.views}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Traffic Sources */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg p-6 transition-colors duration-300">
          <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-6">
            Traffic Sources
          </h2>
          <div className="space-y-4">
            {stats.trafficSources.map((source) => (
              <div key={source.source}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-gray-800 dark:text-gray-100">{source.source}</span>
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {source.visits.toLocaleString()} visits ({source.percentage}%)
                  </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-2.5">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${source.percentage}%` }}
                    transition={{ duration: 1, delay: 0.2 }}
                    className="bg-gradient-to-r from-blue-500 to-purple-500 h-2.5 rounded-full"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Info Note */}
        <div className="mt-8 bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-200 dark:border-blue-800 rounded-xl p-6">
          <h3 className="font-bold text-blue-900 dark:text-blue-300 mb-2">📊 About This Data</h3>
          <p className="text-sm text-blue-800 dark:text-blue-400">
            Currently showing mock data for demonstration. To integrate real Google Analytics data:
          </p>
          <ol className="list-decimal list-inside text-sm text-blue-800 dark:text-blue-400 mt-2 space-y-1">
            <li>Set up Google Analytics 4 (GA4) if not already done</li>
            <li>Create a service account in Google Cloud Console</li>
            <li>Install google-analytics-data package: <code className="bg-blue-100 dark:bg-blue-900/40 px-1 rounded">npm install @google-analytics/data</code></li>
            <li>Implement API calls in a backend service (Django recommended)</li>
            <li>Update this component to fetch real data</li>
          </ol>
        </div>
      </div>
    </div>
  );
}

// Metric Card Component
function MetricCard({ icon, title, value, change, positive, color }) {
  const colorClasses = {
    blue: 'from-blue-500 to-blue-600 dark:from-blue-600 dark:to-blue-700',
    green: 'from-green-500 to-green-600 dark:from-green-600 dark:to-green-700',
    purple: 'from-purple-500 to-purple-600 dark:from-purple-600 dark:to-purple-700',
    orange: 'from-orange-500 to-orange-600 dark:from-orange-600 dark:to-orange-700'
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -5 }}
      className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg p-6 transition-all duration-300"
    >
      <div className="flex items-start justify-between mb-4">
        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${colorClasses[color]} flex items-center justify-center text-white text-xl`}>
          {icon}
        </div>
        {change && (
          <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
            positive 
              ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' 
              : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
          }`}>
            {change}
          </span>
        )}
      </div>
      <h3 className="text-sm text-gray-600 dark:text-gray-400 mb-1">{title}</h3>
      <p className="text-3xl font-bold text-gray-800 dark:text-gray-100">{value}</p>
    </motion.div>
  );
}

